<template>
    <DefaultHeader />

    <main v-if="!$store.state.hiddenBody" class="drivers-page">
        <div class="drivers-container">
            <header class="page-heading">
                <div>
                    <div class="eyebrow">GESTION DES RESSOURCES</div>
                    <h1>Chauffeurs</h1>
                    <p>Retrouvez vos chauffeurs et gérez leurs informations depuis un seul endroit.</p>
                </div>
                <button v-if="canManage" class="btn-primary-custom" type="button" @click="beginCreate">
                    <span aria-hidden="true">＋</span> Nouveau chauffeur
                </button>
            </header>

            <div v-if="pageError" class="message error" role="alert">
                <span>{{ pageError }}</span>
                <button type="button" @click="loadDrivers">Réessayer</button>
            </div>
            <div v-if="feedback" class="message success" role="status">{{ feedback }}</div>

            <div ref="workspace" class="workspace" :class="{ 'has-editor': canManage && editorMode }">
                <section ref="listPanel" class="list-panel" aria-labelledby="drivers-list-title"
                    :tabindex="editorMode ? 0 : undefined"
                    :style="listContentWidth ? { '--list-content-width': `${listContentWidth}px` } : undefined">
                    <div class="panel-toolbar">
                        <div>
                            <h2 id="drivers-list-title">Répertoire</h2>
                            <p>{{ filteredDrivers.length }} chauffeur{{ filteredDrivers.length > 1 ? 's' : '' }}
                                affiché{{ filteredDrivers.length > 1 ? 's' : '' }}</p>
                        </div>
                        <div class="filters">
                            <label class="search-wrap">
                                <span class="sr-only">Rechercher un chauffeur</span>
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m16.5 16.5 4 4" />
                                </svg>
                                <input v-model.trim="search" type="search" placeholder="Rechercher un chauffeur…" />
                            </label>
                            <div class="status-tabs" role="group" aria-label="Filtrer les chauffeurs par statut">
                                <button type="button" :class="{ active: statusFilter === 'ACTIVE' }"
                                    :aria-pressed="statusFilter === 'ACTIVE'"
                                    @click="statusFilter = 'ACTIVE'">Actifs</button>
                                <button type="button" :class="{ active: statusFilter === 'INACTIVE' }"
                                    :aria-pressed="statusFilter === 'INACTIVE'"
                                    @click="statusFilter = 'INACTIVE'">Inactifs</button>
                                <button type="button" :class="{ active: statusFilter === 'ALL' }"
                                    :aria-pressed="statusFilter === 'ALL'" @click="statusFilter = 'ALL'">Tous</button>
                            </div>
                        </div>
                    </div>

                    <div v-if="loading" class="state" role="status">
                        <span class="loader" aria-hidden="true"></span> Chargement des chauffeurs…
                    </div>
                    <div v-else-if="!drivers.length" class="state">
                        <div class="empty-icon" aria-hidden="true">♙</div>
                        <h3>Aucun chauffeur pour le moment</h3>
                        <p>Créez votre premier chauffeur pour commencer à constituer votre répertoire.</p>
                        <button v-if="canManage" class="btn-secondary-custom" type="button" @click="beginCreate">Ajouter
                            un chauffeur</button>
                    </div>
                    <div v-else-if="!filteredDrivers.length" class="state">
                        <h3>Aucun résultat</h3>
                        <p>Essayez une autre recherche ou modifiez le filtre de statut.</p>
                        <button class="text-button" type="button" @click="resetFilters">Effacer les filtres</button>
                    </div>
                    <DriverList v-else :drivers="filteredDrivers" :can-manage="canManage"
                        :selected-id="selectedDriverId" @edit="beginEdit" />
                </section>

                <aside v-if="canManage && editorMode" ref="editorPanel" class="editor-panel"
                    :style="{ '--editor-offset': `${editorOffset}px` }" aria-label="Édition d'un chauffeur">
                    <DriverForm :key="editorKey" :driver="editingDriver" :saving="saving" :deleting="deleting"
                        :error="formError" @save="saveDriver" @delete="deleteDriver" @badges-changed="refreshDrivers"
                        @cancel="closeEditor" />
                </aside>
            </div>
        </div>
    </main>

    <DefaultFooter />
</template>

<script>
import DefaultHeader from '@/components/DefaultHeader.vue';
import DefaultFooter from '@/components/DefaultFooter.vue';
import DriverRepository from '@/repositories/driver.repo.js';
import DriverList from './sections/DriverList.vue';
import DriverForm from './sections/DriverForm.vue';

export default {
    name: 'DriversView',
    components: { DefaultHeader, DefaultFooter, DriverList, DriverForm },
    data() {
        return {
            drivers: [], loading: false, saving: false, deleting: false,
            search: '', statusFilter: 'ACTIVE',
            editorMode: null, selectedDriverId: null, editorKey: 0,
            editorOffset: 0, listContentWidth: 0, editorResizeObserver: null,
            pageError: '', formError: '', feedback: ''
        };
    },
    computed: {
        // Adapter à ton mécanisme de droits global si son nom diffère.
        canManage() { return Boolean(this.$isClientAdmin || this.$isAdmin); },
        editingDriver() {
            return this.editorMode === 'edit'
                ? this.drivers.find(d => d.id === this.selectedDriverId) || null
                : null;
        },
        filteredDrivers() {
            const needle = this.search.toLocaleLowerCase('fr').trim();
            return this.drivers.filter(driver => {
                const matchesStatus = this.statusFilter === 'ALL' || driver.status === this.statusFilter;
                const fullName = `${driver.firstName || ''} ${driver.lastName || ''}`.toLocaleLowerCase('fr');
                const reverseName = `${driver.lastName || ''} ${driver.firstName || ''}`.toLocaleLowerCase('fr');
                return matchesStatus && (!needle || fullName.includes(needle) || reverseName.includes(needle));
            });
        }
    },
    watch: {
        filteredDrivers() {
            this.$nextTick(() => this.positionEditor());
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
        if (typeof ResizeObserver !== 'undefined') {
            this.editorResizeObserver = new ResizeObserver(() => this.positionEditor());
        }
        this.loadDrivers();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.onResize);
        this.editorResizeObserver?.disconnect();
    },
    methods: {
        async loadDrivers({ silent = false } = {}) {
            // Garder le tableau monté lors des modifications et des associations.
            if (!silent) this.loading = true;
            this.pageError = '';
            try {
                this.drivers = await DriverRepository.getDrivers();
            } catch (err) {
                this.pageError = 'Impossible de charger les chauffeurs.';
                console.error(err);
            } finally {
                if (!silent) this.loading = false;
            }
        },
        async refreshDrivers() {
            await this.loadDrivers({ silent: true });
        },
        resetFilters() { this.search = ''; },
        onResize() {
            if (this.editorMode) {
                this.listContentWidth = Math.max(0, (this.$refs.workspace?.clientWidth || 0) - 2);
            }
            this.$nextTick(() => this.positionEditor());
        },
        selectedRow() {
            const rows = this.$refs.listPanel?.querySelectorAll('[data-driver-id]') || [];
            return Array.from(rows).find(row => row.dataset.driverId === String(this.selectedDriverId)) || null;
        },
        prepareEditor(mode, driverId = null) {
            // Mesurer le tableau avant de rétrécir sa zone de lecture.
            if (!this.editorMode) this.listContentWidth = this.$refs.listPanel?.clientWidth || 0;
            this.editorResizeObserver?.disconnect();
            this.editorMode = mode;
            this.selectedDriverId = driverId;
            this.editorOffset = 0;
            this.formError = '';
            this.feedback = '';
            this.editorKey++;
            this.$nextTick(() => {
                if (this.$refs.editorPanel) this.editorResizeObserver?.observe(this.$refs.editorPanel);
                this.positionEditor();
            });
        },
        beginCreate() {
            if (this.saving || this.deleting) return;
            this.prepareEditor('create');
        },
        beginEdit(driver) {
            if (this.saving || this.deleting) return;
            if (this.editorMode === 'edit' && String(this.selectedDriverId) === String(driver.id)) return;
            this.prepareEditor('edit', driver.id);
        },
        positionEditor() {
            const workspace = this.$refs.workspace;
            const editor = this.$refs.editorPanel;
            if (!workspace || !editor) return;

            // En vue étroite, l'éditeur reste sous le tableau, sans repositionnement.
            if (window.matchMedia('(max-width: 1100px)').matches || this.editorMode === 'create') {
                if (this.editorOffset !== 0) this.editorOffset = 0;
                return;
            }
            const row = this.selectedRow();
            if (!row) return;

            const rowRect = row.getBoundingClientRect();
            const workspaceRect = workspace.getBoundingClientRect();
            const rowCenter = rowRect.top + rowRect.height / 2;
            // Si le formulaire est plus grand que la fenêtre, centrer la partie visible.
            const visibleHeight = Math.min(editor.offsetHeight, Math.max(0, window.innerHeight - 48));
            const desiredTop = Math.max(16, rowCenter - visibleHeight / 2);
            const offset = Math.max(0, Math.round(desiredTop - workspaceRect.top));
            if (this.editorOffset !== offset) this.editorOffset = offset;
        },
        closeEditor() {
            if (this.saving || this.deleting) return;
            this.resetEditor();
        },
        resetEditor() {
            this.editorResizeObserver?.disconnect();
            this.editorMode = null;
            this.selectedDriverId = null;
            this.editorOffset = 0;
            this.listContentWidth = 0;
            this.formError = '';
        },
        async saveDriver(payload) {
            if (this.saving || this.deleting) return;
            this.saving = true; this.formError = ''; this.feedback = '';
            const isCreate = this.editorMode === 'create';
            try {
                if (isCreate) await DriverRepository.createDriver(payload);
                else await DriverRepository.updateDriver(this.selectedDriverId, payload);
                await this.loadDrivers({ silent: true });
                if (this.pageError) {
                    this.formError = 'Enregistrement effectué, mais la liste n’a pas pu être actualisée.';
                    return;
                }
                this.feedback = isCreate ? 'Chauffeur créé avec succès.' : 'Chauffeur modifié avec succès.';
                this.resetEditor();
            } catch (err) {
                this.formError = 'Enregistrement impossible. Vérifiez les informations et réessayez.';
                console.error(err);
            } finally {
                this.saving = false;
            }
        },
        async deleteDriver() {
            if (this.editorMode !== 'edit' || this.saving || this.deleting) return;
            this.deleting = true; this.formError = ''; this.feedback = '';
            try {
                await DriverRepository.deleteDriver(this.selectedDriverId);
                await this.loadDrivers({ silent: true });
                if (this.pageError) {
                    this.formError = 'Suppression effectuée, mais la liste n’a pas pu être actualisée.';
                    return;
                }
                this.feedback = 'Chauffeur supprimé.';
                this.resetEditor();
            } catch (err) {
                this.formError = 'Suppression impossible. Veuillez réessayer.';
                console.error(err);
            } finally { this.deleting = false; }
        }
    }
};
</script>

<style scoped>
.drivers-page {
    min-height: 65vh;
    background: #f5f7fb;
    color: #17243b;
    padding: 42px 24px 70px;
}

.drivers-container {
    max-width: 1420px;
    margin: 0 auto;
}

.page-heading {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    align-items: center;
    margin-bottom: 30px;
}

.eyebrow {
    color: #627c9e;
    font-size: 11px;
    letter-spacing: .16em;
    font-weight: 800;
    margin-bottom: 9px;
}

h1 {
    font-size: clamp(28px, 3vw, 36px);
    font-weight: 750;
    letter-spacing: -.035em;
    margin: 0 0 7px;
}

.page-heading p,
.panel-toolbar p {
    margin: 0;
    color: #6c7890;
    font-size: 14px;
}

.btn-primary-custom {
    border: 1px solid #1b4f95;
    background: #1b4f95;
    color: white;
    padding: 12px 18px;
    border-radius: 10px;
    font-weight: 700;
    white-space: nowrap;
    cursor: pointer;
}

.btn-primary-custom:hover {
    background: #153f78;
}

.btn-secondary-custom {
    border: 1px solid #cbd8e8;
    background: white;
    color: #24538d;
    padding: 10px 15px;
    border-radius: 9px;
    cursor: pointer;
    font-weight: 700;
}

/* La liste garde toute la largeur hors édition. Pendant l'édition, sa zone
   de lecture rétrécit mais son contenu conserve sa largeur initiale. */
.workspace {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
    align-items: start;
    overflow-anchor: none;
}

.workspace.has-editor {
    grid-template-columns: minmax(0, 1fr) minmax(340px, 400px);
}

@media (min-width: 1101px) {
    .workspace.has-editor .list-panel {
        overflow-x: auto;
        overflow-y: hidden;
    }

    .workspace.has-editor .panel-toolbar {
        box-sizing: border-box;
        width: var(--list-content-width);
    }

    .workspace.has-editor :deep(.table-scroll) {
        width: var(--list-content-width);
        overflow-x: visible;
    }
}

.list-panel,
.editor-panel {
    min-width: 0;
    background: white;
    border-radius: 16px;
    border: 1px solid #e3e9f1;
    box-shadow: 0 10px 28px rgba(20, 43, 74, .045);
    overflow: hidden;
}

.editor-panel {
    margin-top: var(--editor-offset, 0px);
}

.panel-toolbar {
    padding: 24px 26px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    border-bottom: 1px solid #e9edf3;
}

.panel-toolbar h2 {
    font-size: 18px;
    font-weight: 750;
    margin: 0 0 4px;
}

.filters {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
}

.search-wrap {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid #dce4ee;
    border-radius: 9px;
    padding: 0 12px;
    background: #fff;
    min-width: 220px;
    margin: 0;
}

.search-wrap svg {
    width: 17px;
    height: 17px;
    stroke: #8999ae;
    stroke-width: 2;
    fill: none;
}

.search-wrap input {
    border: 0;
    outline: 0;
    padding: 11px 0;
    background: transparent;
    width: 100%;
    min-width: 0;
    font-size: 13px;
}

.status-tabs {
    display: flex;
    align-items: center;
    padding: 3px;
    border: 1px solid #dce4ee;
    border-radius: 9px;
    background: #f5f7fb;
}

.status-tabs button {
    border: 0;
    background: transparent;
    color: #63728a;
    padding: 8px 13px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}

.status-tabs button.active {
    background: white;
    color: #1b4f95;
    box-shadow: 0 1px 4px rgba(20, 43, 74, .12);
}

.search-wrap:focus-within,
.status-tabs button:focus-visible {
    outline: 2px solid #93b5e3;
    outline-offset: 1px;
}

.state {
    min-height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    text-align: center;
    padding: 35px;
    color: #687892;
}

.state h3 {
    color: #21324b;
    font-size: 18px;
    margin: 0;
}

.state p {
    max-width: 380px;
    margin: 0;
}

.empty-icon {
    display: grid;
    place-items: center;
    background: #f1f5fa;
    color: #7390b2;
    border-radius: 50%;
    width: 64px;
    height: 64px;
    font-size: 35px;
}

.loader {
    width: 27px;
    height: 27px;
    border: 3px solid #dae7f4;
    border-top-color: #1b4f95;
    border-radius: 50%;
    animation: spin .8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.message {
    padding: 13px 16px;
    border-radius: 10px;
    margin-bottom: 18px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
}

.message.error {
    color: #a32929;
    background: #fff0f0;
    border: 1px solid #f5cbcb;
}

.message.success {
    color: #136243;
    background: #eaf7ef;
    border: 1px solid #c4e8d2;
}

.message button,
.text-button {
    padding: 0;
    border: 0;
    background: none;
    cursor: pointer;
    color: inherit;
    font-weight: 700;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

@media (max-width: 1100px) {
    .workspace.has-editor {
        grid-template-columns: 1fr;
    }

    .editor-panel {
        margin-top: 0;
    }
}

@media (max-width: 620px) {
    .drivers-page {
        padding: 26px 14px 50px;
    }

    .page-heading {
        align-items: stretch;
        flex-direction: column;
    }

    .panel-toolbar {
        padding: 20px 16px;
    }

    .filters,
    .search-wrap,
    .status-tabs {
        width: 100%;
    }

    .status-tabs button {
        flex: 1;
    }
}
</style>
